import React, { useContext } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import MuiTab, { TabProps as MuiTabProps } from '@material-ui/core/Tab';
import classnames from 'classnames';

import { capitalizeFirstLetter } from '@lib/services/stringsService';

import { TabsContext } from './Tabs';

// Todo: refactor component to use MakeStyles to make it easier to receive props on styles
const styles = (theme: Theme) =>
  createStyles({
    rootDense: {
      minHeight: theme.spacing.unit * 4,
      minWidth: 'initial',
    },
    labelContainerDense: {
      padding: 0,
    },
    label: {
      fontSize: 12,
      textTransform: 'capitalize',
    },
    labelNormal: {
      fontSize: 16,
      textTransform: 'capitalize',
    },
    selected: {
      fontWeight: 600,
    },
    minWidth: {
      minWidth: 95,
      [theme.breakpoints.down('xs')]: {
        minWidth: 80,
      },
    },
    labelIcon: { paddingTop: 0 },
    iconWrapper: {
      flexDirection: 'row-reverse',
      justifyContent: 'space-evenly',
      marginLeft: 4 * theme.spacing.unit,
      marginRight: 4 * theme.spacing.unit,
    },
    iconLabelContainer: { padding: 0, width: 'max-content' },
    colorWhite: { color: theme.palette.common.white },
    colorSupportMidnight: { color: theme.custom.palette.colorSupportMidnight },
    backgroundColorSupportMidnight: { backgroundColor: theme.custom.palette.colorSupportMidnight },
    backgroundColorWhite: { backgroundColor: theme.palette.common.white },
    invertSelectedColors: { opacity: 1 },
  });

// Adding props for color, backgroundColor and fontSize so this component can be reused on other places, like fixedFilters
export type colorKey = 'white';
export type backgroundColorKey = 'supportMidnight';
export type fontSizeKey = 'small' | 'normal';

export interface TabProps extends Omit<MuiTabProps, 'classes'>, WithStyles<typeof styles> {
  classes: any;
  minWidth?: boolean;
  color?: colorKey;
  backgroundColor?: backgroundColorKey;
  invertSelectedColors?: boolean;
  fontSize?: fontSizeKey;
}

const Tab: React.FC<TabProps> = ({
  classes,
  selected,
  minWidth,
  color,
  backgroundColor,
  invertSelectedColors,
  fontSize = 'small',
  ...rest
}) => {
  const { dense } = useContext(TabsContext);

  return (
    <MuiTab
      classes={{
        root: classnames({
          [classes.rootDense]: dense,
          [classes.minWidth]: minWidth,
          [classes[`backgroundColor${capitalizeFirstLetter(backgroundColor)}`]]: backgroundColor,
          [classes.invertSelectedColors]: invertSelectedColors,
        }),
        labelContainer: classnames({
          [classes.labelContainerDense]: dense,
          [classes.iconLabelContainer]: rest.icon,
        }),
        label: classnames({
          [classes.label]: fontSize === 'small',
          [classes.labelNormal]: fontSize === 'normal',
          [classes[`color${capitalizeFirstLetter(color)}`]]: color && (!selected || !invertSelectedColors),
          [classes[`color${capitalizeFirstLetter(backgroundColor)}`]]:
            backgroundColor && invertSelectedColors && selected,
        }),
        labelIcon: classnames(
          {
            [classes[`color${capitalizeFirstLetter(color)}`]]: color && (!selected || !invertSelectedColors),
            [classes[`color${capitalizeFirstLetter(backgroundColor)}`]]:
              backgroundColor && invertSelectedColors && selected,
          },
          classes.labelIcon,
        ),
        wrapper: classnames({
          [classes.iconWrapper]: rest.icon,
        }),
        selected: classnames(classes.selected, {
          [classes[`backgroundColor${capitalizeFirstLetter(color)}`]]: color && invertSelectedColors,
        }),
      }}
      selected={selected}
      {...rest}
    />
  );
};

export default withStyles(styles, { name: 'LumaTab' })(Tab);
